import React from 'react';
import { LegalLayout, LegalSection, COMPANY } from '../components/LegalLayout';

export const PrivacidadePage = () => (
  <LegalLayout
    activeTab="privacy"
    title="Política de Privacidade"
    intro={`A ${COMPANY.name} respeita sua privacidade e o tratamento dos dados pessoais de quem usa o MeAdd. Esta política explica o que coletamos, por quê, como armazenamos e seus direitos como titular.`}
    lastUpdated="07 de junho de 2026"
  >
    <LegalSection id="quem-somos" title="1. Quem somos">
      <p>
        O MeAdd é uma plataforma de organização, mobilização e controle interno para campanhas
        políticas, operada por <strong>{COMPANY.name}</strong>, inscrita no CNPJ sob nº {COMPANY.cnpj},
        com sede em {COMPANY.address}, {COMPANY.district}, {COMPANY.city}/{COMPANY.state},
        CEP {COMPANY.zip}.
      </p>
      <p>
        Atuamos como <strong>controlador</strong> dos dados de nossos clientes diretos
        (campanhas/operadores) e como <strong>operador</strong> dos dados de apoiadores e
        lideranças que são cadastrados pelas campanhas em sua plataforma.
      </p>
    </LegalSection>

    <LegalSection id="dados-coletados" title="2. Quais dados coletamos">
      <p>Coletamos diferentes categorias de dados, conforme o seu papel:</p>
      <h3 className="font-semibold text-slate-900 mt-3">2.1 Operadores da campanha (clientes diretos)</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>Nome, e-mail, telefone, cargo na campanha</li>
        <li>Nome da campanha / pessoa jurídica responsável e dados de pagamento (processados pelo Stripe)</li>
        <li>Dados de uso da plataforma (logs de acesso, IP, navegador, ações realizadas)</li>
      </ul>
      <h3 className="font-semibold text-slate-900 mt-3">2.2 Lideranças cadastradas pelas campanhas</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>Nome, e-mail, telefone, cidade, bairro, links pessoais gerados</li>
        <li>Foto, perfil em redes sociais (quando voluntariamente informados)</li>
        <li>Histórico de apoiadores cadastrados sob sua influência</li>
      </ul>
      <h3 className="font-semibold text-slate-900 mt-3">2.3 Apoiadores</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>Nome, WhatsApp, cidade, bairro, tema de interesse declarado</li>
        <li>Liderança ou canal de origem do cadastro</li>
        <li>Aceite registrado da Política de Privacidade e finalidade do contato</li>
        <li>Data e hora do cadastro, IP de origem (para fins de auditoria)</li>
      </ul>
      <p className="text-sm text-slate-600 mt-3">
        Não coletamos intencionalmente dados sensíveis (origem racial, opinião política
        explícita, religião, saúde, vida sexual, biometria, etc.). Caso uma campanha registre
        tais dados em campos abertos, ela atuará como controladora exclusiva desse tratamento.
      </p>
    </LegalSection>

    <LegalSection id="finalidade" title="3. Para que usamos seus dados">
      <ul className="list-disc pl-6 space-y-1">
        <li>Permitir o funcionamento da plataforma (cadastro, autenticação, gestão de equipe)</li>
        <li>Conectar apoiadores às lideranças e canais de comunicação da campanha</li>
        <li>Gerar relatórios internos de mobilização e prestação auxiliar para a equipe</li>
        <li>Prevenir fraudes, abusos e violações dos Termos de Uso</li>
        <li>Cumprir obrigações legais e regulatórias</li>
        <li>Melhorar a plataforma e desenvolver novos recursos (dados agregados/anonimizados)</li>
      </ul>
    </LegalSection>

    <LegalSection id="base-legal" title="4. Bases legais (LGPD)">
      <p>Tratamos dados pessoais com fundamento nas seguintes hipóteses do art. 7º da LGPD:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Consentimento</strong> (art. 7º, I) — apoiadores manifestam aceite expresso no cadastro</li>
        <li><strong>Execução de contrato</strong> (art. 7º, V) — operadores que assinam plano</li>
        <li><strong>Cumprimento de obrigação legal</strong> (art. 7º, II) — registros fiscais e auditoria</li>
        <li><strong>Legítimo interesse</strong> (art. 7º, IX) — segurança, prevenção de fraude e melhoria do serviço</li>
      </ul>
    </LegalSection>

    <LegalSection id="compartilhamento" title="5. Com quem compartilhamos">
      <p>Compartilhamos dados estritamente com fornecedores essenciais à operação:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Cloudinary</strong> — armazenamento de imagens (logo, comprovantes financeiros) </li>
        <li><strong>Resend</strong> — envio de e-mails transacionais (convites, notificações, recuperação de senha)</li>
        <li><strong>Stripe</strong> — processamento de pagamentos recorrentes da assinatura</li>
        <li><strong>Provedor de infraestrutura</strong> — hospedagem da aplicação e do banco de dados</li>
      </ul>
      <p>
        Todos os fornecedores acima estão sujeitos a contratos de tratamento de dados e
        possuem políticas de privacidade próprias. <strong>Não vendemos seus dados.</strong>
      </p>
      <p>
        Poderemos também compartilhar dados mediante <strong>ordem judicial</strong>, requisição
        de autoridade competente ou para defesa de direitos da {COMPANY.name} ou de terceiros.
      </p>
    </LegalSection>

    <LegalSection id="cookies" title="6. Cookies e tecnologias semelhantes">
      <p>Utilizamos cookies essenciais para:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Manter você autenticado (cookies de sessão HTTP-only)</li>
        <li>Identificar preferências de exibição</li>
        <li>Coletar métricas anônimas de uso para melhoria do produto</li>
      </ul>
      <p>
        Você pode bloquear cookies nas configurações do navegador, mas isso pode afetar o
        funcionamento da plataforma.
      </p>
    </LegalSection>

    <LegalSection id="direitos" title="7. Seus direitos como titular">
      <p>Você tem direito a, a qualquer momento:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Confirmar o tratamento e acessar seus dados</li>
        <li>Corrigir dados incompletos, inexatos ou desatualizados</li>
        <li>Solicitar a anonimização, bloqueio ou eliminação de dados desnecessários</li>
        <li>Solicitar a portabilidade dos seus dados</li>
        <li>Revogar o consentimento e descadastrar-se de comunicações</li>
        <li>Obter informações sobre compartilhamento com terceiros</li>
        <li>Apresentar reclamação à ANPD (Autoridade Nacional de Proteção de Dados)</li>
      </ul>
      <p>
        Para exercer seus direitos, escreva para nosso Encarregado:{' '}
        <a className="text-emerald-700 hover:underline" href={`mailto:${COMPANY.dpo_email}`}>
          {COMPANY.dpo_email}
        </a>. Responderemos em até 15 dias úteis.
      </p>
    </LegalSection>

    <LegalSection id="seguranca" title="8. Segurança da informação">
      <p>
        Adotamos medidas técnicas e organizacionais razoáveis para proteger os dados, incluindo
        criptografia em trânsito (TLS), controle de acesso por perfis, autenticação por senha
        com hash, logs de auditoria e backups periódicos.
      </p>
      <p>
        Apesar dos cuidados, nenhum sistema é 100% imune a incidentes. Em caso de violação de
        dados que possa acarretar risco aos titulares, notificaremos os afetados e a ANPD,
        conforme art. 48 da LGPD.
      </p>
    </LegalSection>

    <LegalSection id="retencao" title="9. Retenção dos dados">
      <p>Mantemos seus dados pelo tempo necessário para:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Cumprir a finalidade do tratamento</li>
        <li>Atender obrigações legais (5 anos para registros fiscais e contábeis)</li>
        <li>Exercer regularmente direitos em processos administrativos ou judiciais</li>
      </ul>
      <p>
        Após esse prazo, os dados são anonimizados ou eliminados, salvo quando houver outra base
        legal que justifique sua manutenção.
      </p>
    </LegalSection>

    <LegalSection id="transferencia" title="10. Transferência internacional">
      <p>
        Alguns dos nossos fornecedores (Cloudinary, Stripe, Resend) podem armazenar dados em
        servidores fora do Brasil. Tais transferências ocorrem em países com nível de proteção
        adequado ou mediante cláusulas contratuais específicas, conforme art. 33 da LGPD.
      </p>
    </LegalSection>

    <LegalSection id="alteracoes" title="11. Alterações desta Política">
      <p>
        Esta Política pode ser atualizada para refletir mudanças legais, operacionais ou
        tecnológicas. Sempre que houver alteração relevante, comunicaremos por e-mail aos
        titulares cadastrados e atualizaremos a data no topo do documento.
      </p>
    </LegalSection>

    <LegalSection id="contato" title="12. Fale com a gente">
      <p>
        Dúvidas, sugestões ou solicitações relacionadas à privacidade podem ser enviadas para:
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li>E-mail geral: <a className="text-emerald-700 hover:underline" href={`mailto:${COMPANY.contact_email}`}>{COMPANY.contact_email}</a></li>
        <li>Encarregado (DPO): <a className="text-emerald-700 hover:underline" href={`mailto:${COMPANY.dpo_email}`}>{COMPANY.dpo_email}</a></li>
        <li>Endereço postal: {COMPANY.address}, {COMPANY.district}, {COMPANY.city}/{COMPANY.state} — CEP {COMPANY.zip}</li>
      </ul>
    </LegalSection>
  </LegalLayout>
);

export default PrivacidadePage;
